/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */
import { memo, useEffect, useMemo, useState, type NamedExoticComponent } from 'react';

import { useIntl } from 'react-intl';

import { IconButton } from '@atlaskit/button/new';
import Checkbox from '@atlaskit/checkbox';
import { cssMap, cx, jsx } from '@atlaskit/css';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import { type ExtensionHandlers } from '@atlaskit/editor-common/extensions';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import CopyIcon from '@atlaskit/icon/core/copy';
import TrashIcon from '@atlaskit/icon/core/delete';
import EditIcon from '@atlaskit/icon/core/edit';
import LinkIcon from '@atlaskit/icon/core/link';
import ShowMoreVerticalIcon from '@atlaskit/icon/core/show-more-vertical';
import StarFilledIcon from '@atlaskit/icon/core/star-starred';
import StarUnstarredIcon from '@atlaskit/icon/core/star-unstarred';
import { RovoIcon } from '@atlaskit/logo';
import { FadeIn, StaggeredEntrance } from '@atlaskit/motion';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { ReactRenderer } from '@atlaskit/renderer';
import { token } from '@atlaskit/tokens';
import { AssignedAppsIcons } from '@atlassian/conversation-assistant-content-renderer/ui/message-renderer/SourceButtonIcons';
import { encodePromptToAdf } from '@atlassian/conversation-assistant-content-transformer/adf-encoder';
import { ROVO_SKILL_EXTENSION_TYPE } from '@atlassian/conversation-assistant-content-transformer/utils/skills';
import type { AssistanceServiceInputParam } from '@atlassian/conversation-assistant-service/services/assistance-service/main';
import { SkillTag } from '@atlassian/rovo-platform-ui-components/skills/SkillTag';
import type { SkillMetadataMap } from '@atlassian/rovo-skills/types';

import { type PromptCard } from '../../controllers/prompt-cards';
import { getUseCaseIcon } from '../../controllers/prompt-use-case-icons';
import { DeleteConfirmModal } from '../rovo-prompt-library-toolbar/DeleteConfirmModal';

import { PromptCardsEmptyState } from './empty-state';
import { messages } from './messages';

const styles = cssMap({
	card: {
		backgroundColor: token('elevation.surface'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderRadius: token('radius.xlarge'),
		paddingTop: token('space.250'),
		paddingBottom: token('space.250'),
		paddingLeft: token('space.200'),
		paddingRight: token('space.200'),
		cursor: 'pointer',
		width: '245px',
		height: '150px',
		textAlign: 'left',
		transition: 'transform 0.15s ease-out, box-shadow 0.15s ease-out',
		boxSizing: 'border-box',
		display: 'flex',
		flexDirection: 'column',
		overflow: 'hidden',
	},
	cardHover: {
		'&:hover': {
			transform: 'translateY(-2px)',
			boxShadow: token('elevation.shadow.overlay'),
		},
	},
	cardSelected: {
		borderColor: token('color.border.focused'),
	},
	cardNewChatDashed: {
		borderStyle: 'dashed',
		'&:hover': {
			backgroundColor: token('elevation.surface.raised.hovered'),
		},
	},
	cardWithAppsSection: {
		height: '170px',
	},

	cardHeader: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'flex-start',
		marginBottom: token('space.100'),
	},

	iconContainer: {
		width: '32px',
		height: '32px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	checkboxContainer: {
		marginLeft: token('space.negative.050'),
		marginTop: token('space.negative.050'),
	},
	optionsMenuButton: {
		marginTop: token('space.negative.050'),
		marginRight: token('space.negative.050'),
		opacity: 0,
		transition: 'opacity 0.15s ease-out',
	},
	optionsMenuButtonVisible: {
		opacity: 1,
	},

	title: {
		color: token('color.text.subtle'),
		marginBottom: token('space.050'),
		textAlign: 'left',
		fontFamily: token('font.family.body'),
		fontWeight: token('font.weight.medium'),
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
	},

	cardContentWrapper: {
		display: 'flex',
		flexDirection: 'column',
		flexGrow: 1,
	},
	assignedApps: {
		marginTop: 'auto',
		paddingTop: token('space.100'),
		display: 'flex',
		alignItems: 'center',
		gap: token('space.075'),
	},
	rovoPlaceholderIcon: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},

	grid: {
		display: 'grid',
		gridTemplateColumns: 'repeat(auto-fit, 245px)',
		gap: token('space.200'),
		alignContent: 'start',
		paddingTop: token('space.200'),
		paddingBottom: token('space.300'),
		paddingLeft: token('space.100'),
		maxWidth: 'min(800px, 98vw)',
		minHeight: '450px',
	},
	gridLandingPage: {
		paddingLeft: 0,
		paddingTop: 0,
		minHeight: 'auto',
	},
	gridFullWidth: {
		gridTemplateColumns: '1fr',
	},
	cardFullWidth: {
		width: '100%',
		height: 'auto',
	},
	cardHeaderFullWidth: {
		alignItems: 'center',
	},
	titleInline: {
		flexGrow: 1,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		alignSelf: 'flex-end',
		paddingLeft: token('space.200'),
	},
});

// Extension handler that renders skill inlineExtension nodes as SkillTag components in the preview.
export const skillExtensionHandlers: ExtensionHandlers = {
	[ROVO_SKILL_EXTENSION_TYPE]: (ext) => {
		const params = ext.parameters;
		if (params) {
			return <SkillTag {...params} disableHovercard />;
		}
		return null;
	},
};

type PromptCardProps = {
	card: PromptCard;
	hideAppsSection?: boolean;
	onClick?: (card: PromptCard) => void;
	onHover?: (card: PromptCard | null) => void;
	onDeletePrompt?: (promptId: string) => Promise<void>;
	onEditPrompt?: (card: PromptCard) => void;
	onDuplicatePrompt?: (card: PromptCard) => void;
	onFavoriteToggle?: (
		promptId: string,
		isFavorited: boolean,
		isUserGenerated?: boolean,
	) => Promise<void>;
	onSharePrompt?: (card: PromptCard) => void;
	/** Only shown for user-generated cards. */
	showSelectCheckbox?: boolean;
	isSelected?: boolean;
	onSelectToggle?: (cardId: string) => void;
	assistanceServiceParams?: AssistanceServiceInputParam['assistanceServiceParams'];
	skillMetadata?: SkillMetadataMap;
	fullWidth?: boolean;
};

export const PromptCardComponent: NamedExoticComponent<PromptCardProps> = memo<PromptCardProps>(
	({
		card,
		onClick,
		onHover,
		onDeletePrompt,
		onEditPrompt,
		onDuplicatePrompt,
		onFavoriteToggle,
		onSharePrompt,
		showSelectCheckbox,
		isSelected,
		onSelectToggle,
		assistanceServiceParams,
		skillMetadata,
		fullWidth,
		hideAppsSection,
	}) => {
		const { formatMessage } = useIntl();
		const [isCardHovered, setIsCardHovered] = useState(false);
		const [isCardFocused, setIsCardFocused] = useState(false);
		const [isDropdownOpen, setIsDropdownOpen] = useState(false);
		const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
		const [isDeleting, setIsDeleting] = useState(false);
		const [isFavorited, setIsFavorited] = useState(card.isFavorited ?? false);
		const [isTogglingFavorite, setIsTogglingFavorite] = useState(false);
		const Icon = card.icon ?? getUseCaseIcon(card.useCase);
		const selectedAppIds = card.appIds ?? [];
		const appCount = selectedAppIds.length;
		useEffect(() => {
			setIsFavorited(card.isFavorited ?? false);
		}, [card.isFavorited]);

		const handleDeleteConfirm = async () => {
			setIsDeleting(true);
			try {
				await onDeletePrompt?.(card.id);
				setIsDeleteConfirmOpen(false);
			} catch {
				// Keep modal open on error so user can retry or cancel
			} finally {
				setIsDeleting(false);
			}
		};

		const handleDeleteCancel = () => {
			setIsDeleteConfirmOpen(false);
		};

		const handleFavoriteToggle = async (e: React.MouseEvent) => {
			e.stopPropagation();
			setIsTogglingFavorite(true);

			// Optimistic update: immediately change the UI state
			const newFavoritedState = !isFavorited;
			const previousFavoritedState = isFavorited;
			setIsFavorited(newFavoritedState);

			try {
				await onFavoriteToggle?.(card.id, newFavoritedState, card.isUserGenerated);
			} catch {
				// Rollback to previous state on error
				setIsFavorited(previousFavoritedState);
			} finally {
				setIsTogglingFavorite(false);
			}
		};

		const adfDocument = useMemo(() => {
			return encodePromptToAdf(card.prompt, skillMetadata).adf;
		}, [card.prompt, skillMetadata]);

		const showActionsVisible = isCardHovered || isCardFocused || isDropdownOpen;
		const showCheckboxInPlaceOfIcon = showSelectCheckbox && (showActionsVisible || isSelected);

		const cardContent = (
			<Box xcss={styles.cardContentWrapper}>
				<Stack space="space.075">
					<Box xcss={cx(styles.cardHeader, fullWidth && styles.cardHeaderFullWidth)}>
						{showCheckboxInPlaceOfIcon ? (
							<Box xcss={styles.checkboxContainer}>
								<Checkbox
									isChecked={isSelected}
									onChange={() => {
										onSelectToggle?.(card.id);
									}}
									aria-label={formatMessage(messages.selectPrompt, { title: card.title })}
								/>
							</Box>
						) : (
							<Box xcss={styles.iconContainer}>
								<Icon />
							</Box>
						)}
						{fullWidth && !showCheckboxInPlaceOfIcon && (
							<Box xcss={cx(styles.title, styles.titleInline)}>{card.title}</Box>
						)}
						<Inline space="space.050" alignBlock="center">
							{onFavoriteToggle && (
								<Box
									xcss={cx(
										styles.optionsMenuButton,
										showActionsVisible && styles.optionsMenuButtonVisible,
									)}
								>
									<IconButton
										spacing="compact"
										appearance="subtle"
										icon={() =>
											isFavorited ? (
												<StarFilledIcon label="" color={token('color.icon.accent.orange')} />
											) : (
												<StarUnstarredIcon label="" color={token('color.icon')} />
											)
										}
										label={
											isFavorited
												? formatMessage(messages.unfavoritePrompt)
												: formatMessage(messages.favoritePrompt)
										}
										isDisabled={isTogglingFavorite}
										onClick={handleFavoriteToggle}
									/>
								</Box>
							)}
							{onSharePrompt && (
								<Box
									xcss={cx(
										styles.optionsMenuButton,
										showActionsVisible && styles.optionsMenuButtonVisible,
									)}
								>
									<IconButton
										spacing="compact"
										appearance="subtle"
										icon={() => <LinkIcon label="" color={token('color.icon')} />}
										label={formatMessage(messages.sharePrompt)}
										onClick={(e) => {
											e.stopPropagation();
											onSharePrompt(card);
										}}
									/>
								</Box>
							)}
							{(onEditPrompt || onDuplicatePrompt || onDeletePrompt) && (
								<Box
									xcss={cx(
										styles.optionsMenuButton,
										showActionsVisible && styles.optionsMenuButtonVisible,
									)}
								>
									<DropdownMenu<HTMLButtonElement>
										onOpenChange={({ isOpen }) => setIsDropdownOpen(isOpen)}
										trigger={({ triggerRef, ...props }) => (
											<IconButton
												{...props}
												ref={triggerRef}
												spacing="compact"
												appearance="subtle"
												icon={() => <ShowMoreVerticalIcon label="" />}
												label={formatMessage(messages.cardMoreActions)}
												onClick={(e) => {
													e.stopPropagation();
													props.onClick?.(e);
												}}
											/>
										)}
										shouldFlip
									>
										<DropdownItemGroup>
											<DropdownItem
												elemBefore={<EditIcon label="" />}
												onClick={(e) => {
													e.stopPropagation();
													onEditPrompt?.(card);
												}}
											>
												{formatMessage(messages.edit)}
											</DropdownItem>
											<DropdownItem
												elemBefore={<CopyIcon label="" />}
												onClick={(e) => {
													e.stopPropagation();
													onDuplicatePrompt?.(card);
												}}
											>
												{formatMessage(messages.duplicate)}
											</DropdownItem>
											{onDeletePrompt && (
												<DropdownItem
													elemBefore={<TrashIcon label="" color={token('color.icon.danger')} />}
													onClick={(e) => {
														e.stopPropagation();
														setIsDeleteConfirmOpen(true);
													}}
												>
													<Text color="color.text.danger">{formatMessage(messages.delete)}</Text>
												</DropdownItem>
											)}
										</DropdownItemGroup>
									</DropdownMenu>
								</Box>
							)}
						</Inline>
					</Box>
					{!fullWidth && <Box xcss={styles.title}>{card.title}</Box>}
					<ReactRenderer
						document={adfDocument}
						extensionHandlers={skillExtensionHandlers}
						nodeComponents={{
							paragraph: ({ children }) => (
								<Text color="color.text.subtlest" size="small" maxLines={2}>
									{children}
								</Text>
							),
							heading: ({ children }) => (
								<Text color="color.text.subtlest" size="small" maxLines={2}>
									{children}
								</Text>
							),
							rule: () => null,
						}}
					/>
				</Stack>
				{!hideAppsSection &&
					FeatureGates.getExperimentValue(
						'rovo_chat_prompt_library_v2_exp',
						'isEnabled',
						false,
					) && (
						<Box xcss={styles.assignedApps}>
							{appCount > 0 && assistanceServiceParams ? (
								<AssignedAppsIcons
									appIds={selectedAppIds}
									assistanceServiceParams={assistanceServiceParams}
								/>
							) : (
								<Inline space="space.050" alignBlock="center">
									<Box xcss={styles.rovoPlaceholderIcon}>
										<RovoIcon label="" shouldUseHexLogo size="xxsmall" />
									</Box>
									<Text color="color.text.subtlest" size="small">
										Rovo
									</Text>
								</Inline>
							)}
						</Box>
					)}
			</Box>
		);

		return (
			<>
				<Box
					xcss={cx(
						styles.card,
						fullWidth && styles.cardFullWidth,
						isSelected && styles.cardSelected,
						card.startNewChat ? styles.cardNewChatDashed : styles.cardHover,
						/* eslint-disable-next-line @atlaskit/platform/use-recommended-utils */
						!hideAppsSection &&
							FeatureGates.getExperimentValue(
								'rovo_chat_prompt_library_v2_exp',
								'isEnabled',
								false,
							) &&
							styles.cardWithAppsSection,
					)}
					onClick={(e: React.MouseEvent<HTMLDivElement>) => {
						// Don't trigger card click if click originated from an interactive element
						if (
							(e.target as HTMLElement).closest('input[type="checkbox"], button, [role="button"]')
						) {
							return;
						}
						onClick?.(card);
					}}
					onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
						// Handle Enter/Space for keyboard accessibility
						if (e.key === 'Enter' || e.key === ' ') {
							// Don't trigger if focused on an interactive child element
							if (
								(e.target as HTMLElement).closest('input[type="checkbox"], button, [role="button"]')
							) {
								return;
							}
							e.preventDefault();
							onClick?.(card);
						}
					}}
					onMouseEnter={() => {
						setIsCardHovered(true);
						onHover?.(card);
					}}
					onMouseLeave={() => {
						setIsCardHovered(false);
						onHover?.(null);
					}}
					tabIndex={0}
					aria-label={card.title}
					onFocus={(e: React.FocusEvent<HTMLDivElement>) => {
						if (e.target.matches(':focus-visible')) {
							setIsCardFocused(true);
							onHover?.(card);
						}
					}}
					onBlur={() => {
						if (isCardFocused) {
							setIsCardFocused(false);
							onHover?.(null);
						}
					}}
				>
					{cardContent}
				</Box>
				<DeleteConfirmModal
					title={formatMessage(messages.deleteConfirmTitle)}
					body={formatMessage(messages.deleteConfirmBody)}
					isOpen={isDeleteConfirmOpen}
					isLoading={isDeleting}
					onClose={handleDeleteCancel}
					onConfirm={handleDeleteConfirm}
					testId="delete-prompt-confirm-modal"
				/>
			</>
		);
	},
);

type PromptCardsGridProps = {
	cards: PromptCard[];
	onCardClick?: (card: PromptCard) => void;
	onCardHover?: (card: PromptCard | null) => void;
	onDeletePrompt?: (promptId: string) => Promise<void>;
	onEditPrompt?: (card: PromptCard) => void;
	onDuplicatePrompt?: (card: PromptCard) => void;
	onFavoriteToggle?: (
		promptId: string,
		isFavorited: boolean,
		isUserGenerated?: boolean,
	) => Promise<void>;
	onSharePrompt?: (card: PromptCard) => void;
	isLandingPage?: boolean;
	fullWidth?: boolean;
	selectedCardIds?: Set<string>;
	onSelectionChange?: (selectedIds: Set<string>) => void;
	assistanceServiceParams?: AssistanceServiceInputParam['assistanceServiceParams'];
	skillMetadata?: SkillMetadataMap;
	hideAppsSection?: boolean;
};

export const PromptCardsGrid: NamedExoticComponent<PromptCardsGridProps> =
	memo<PromptCardsGridProps>(
		({
			cards,
			onCardClick,
			onCardHover,
			onDeletePrompt,
			onEditPrompt,
			onDuplicatePrompt,
			onFavoriteToggle,
			onSharePrompt,
			isLandingPage,
			fullWidth,
			selectedCardIds,
			onSelectionChange,
			assistanceServiceParams,
			skillMetadata,
			hideAppsSection,
		}) => {
			const enableMultiselect =
				selectedCardIds !== undefined &&
				onSelectionChange !== undefined &&
				(onDeletePrompt != null || onFavoriteToggle != null);

			const handleSelectToggle = (cardId: string) => {
				if (!onSelectionChange || !selectedCardIds) return;
				const next = new Set(selectedCardIds);
				if (next.has(cardId)) {
					next.delete(cardId);
				} else {
					next.add(cardId);
				}
				onSelectionChange(next);
			};

			if (cards.length === 0) {
				return <PromptCardsEmptyState />;
			}

			return (
				<Box
					xcss={cx(
						styles.grid,
						isLandingPage && styles.gridLandingPage,
						fullWidth && styles.gridFullWidth,
					)}
				>
					<StaggeredEntrance columns={1} delayStep={100}>
						{cards.map((card) => (
							<FadeIn key={card.id} duration="medium">
								{(props) => (
									<div {...props}>
										<PromptCardComponent
											card={card}
											onClick={onCardClick}
											onHover={onCardHover}
											onDeletePrompt={onDeletePrompt}
											onEditPrompt={onEditPrompt}
											onDuplicatePrompt={onDuplicatePrompt}
											onFavoriteToggle={onFavoriteToggle}
											onSharePrompt={onSharePrompt}
											showSelectCheckbox={enableMultiselect}
											isSelected={selectedCardIds?.has(card.id)}
											onSelectToggle={enableMultiselect ? handleSelectToggle : undefined}
											assistanceServiceParams={assistanceServiceParams}
											skillMetadata={skillMetadata}
											fullWidth={fullWidth}
											hideAppsSection={hideAppsSection}
										/>
									</div>
								)}
							</FadeIn>
						))}
					</StaggeredEntrance>
				</Box>
			);
		},
	);
